/**
 * Reversi AI Player
 *
 * Three difficulty levels:
 * - Easy: random valid move, 500ms delay
 * - Medium: greedy — pick move that flips most discs, 1s delay
 * - Hard: minimax with alpha-beta pruning (depth 4-6),
 *         positional evaluation (corners +100, edges +10,
 *         adjacent-to-corners -20, other +1), 2s delay
 */

import {
  CellState,
  PlayerColor,
  Position,
  ReversiDifficulty,
  BOARD_SIZE,
  DIRECTIONS,
} from './types';

/**
 * Positional weight map for the 8×8 board.
 * Corners are most valuable, edges are good,
 * squares adjacent to corners (X-squares and C-squares) are bad.
 */
const POSITION_WEIGHTS: number[][] = [
  [100, -20,  10,  10,  10,  10, -20, 100],
  [-20, -20,   1,   1,   1,   1, -20, -20],
  [ 10,   1,   1,   1,   1,   1,   1,  10],
  [ 10,   1,   1,   1,   1,   1,   1,  10],
  [ 10,   1,   1,   1,   1,   1,   1,  10],
  [ 10,   1,   1,   1,   1,   1,   1,  10],
  [-20, -20,   1,   1,   1,   1, -20, -20],
  [100, -20,  10,  10,  10,  10, -20, 100],
];

export class ReversiAI {
  private difficulty: ReversiDifficulty;
  private pendingTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(difficulty: ReversiDifficulty = 'medium') {
    this.difficulty = difficulty;
  }

  /** Get the delay in ms before the AI makes a move */
  getDelay(): number {
    switch (this.difficulty) {
      case 'easy':
        return 500;
      case 'medium':
        return 1000;
      case 'hard':
        return 2000;
      default:
        return 1000;
    }
  }

  setDifficulty(difficulty: ReversiDifficulty): void {
    this.difficulty = difficulty;
  }

  getDifficulty(): ReversiDifficulty {
    return this.difficulty;
  }

  /**
   * Choose the best move for the given player on the given board.
   * Returns null if no valid moves exist.
   */
  chooseMove(board: CellState[][], player: PlayerColor): Position | null {
    const validMoves = this.getValidMoves(board, player);
    if (validMoves.length === 0) return null;

    switch (this.difficulty) {
      case 'easy':
        return this.chooseMoveEasy(validMoves);
      case 'medium':
        return this.chooseMoveMedium(board, player, validMoves);
      case 'hard':
        return this.chooseMoveHard(board, player, validMoves);
      default:
        return this.chooseMoveEasy(validMoves);
    }
  }

  /**
   * Schedule an AI move after the configured delay.
   */
  scheduleMove(
    board: CellState[][],
    player: PlayerColor,
    onMove: (position: Position | null) => void
  ): () => void {
    this.cancelPendingMove();

    const delay = this.getDelay();
    this.pendingTimeout = setTimeout(() => {
      this.pendingTimeout = null;
      const move = this.chooseMove(board, player);
      onMove(move);
    }, delay);

    return () => this.cancelPendingMove();
  }

  cancelPendingMove(): void {
    if (this.pendingTimeout !== null) {
      clearTimeout(this.pendingTimeout);
      this.pendingTimeout = null;
    }
  }

  destroy(): void {
    this.cancelPendingMove();
  }

  // =========================================================================
  // Easy: Random valid move
  // =========================================================================

  private chooseMoveEasy(validMoves: Position[]): Position {
    const index = Math.floor(Math.random() * validMoves.length);
    return validMoves[index];
  }

  // =========================================================================
  // Medium: Greedy — pick move that flips the most discs
  // =========================================================================

  private chooseMoveMedium(
    board: CellState[][],
    player: PlayerColor,
    validMoves: Position[]
  ): Position {
    let bestMove = validMoves[0];
    let bestFlips = 0;

    for (const move of validMoves) {
      const flips = this.getFlippableDiscs(board, player, move).length;
      if (flips > bestFlips) {
        bestFlips = flips;
        bestMove = move;
      }
    }

    return bestMove;
  }

  // =========================================================================
  // Hard: Minimax with alpha-beta pruning
  // =========================================================================

  private chooseMoveHard(
    board: CellState[][],
    player: PlayerColor,
    validMoves: Position[]
  ): Position {
    const depth = this.getSearchDepth(board);
    let bestMove = validMoves[0];
    let bestScore = -Infinity;

    for (const move of validMoves) {
      const newBoard = this.applyMove(board, player, move);
      const score = this.minimax(
        newBoard,
        depth - 1,
        -Infinity,
        Infinity,
        false,
        player
      );
      if (score > bestScore) {
        bestScore = score;
        bestMove = move;
      }
    }

    return bestMove;
  }

  /**
   * Determine search depth based on how many empty squares remain.
   * More empty squares = shallower search for performance.
   */
  private getSearchDepth(board: CellState[][]): number {
    let empty = 0;
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] === 'empty') empty++;
      }
    }

    // Near end-game, search deeper
    if (empty <= 10) return 8;
    if (empty <= 16) return 6;
    return 4;
  }

  private minimax(
    board: CellState[][],
    depth: number,
    alpha: number,
    beta: number,
    isMaximizing: boolean,
    aiPlayer: PlayerColor
  ): number {
    const opponent = aiPlayer === 'black' ? 'white' : 'black';
    const currentPlayer = isMaximizing ? aiPlayer : opponent;

    // Terminal conditions
    if (depth === 0) {
      return this.evaluateBoard(board, aiPlayer);
    }

    const validMoves = this.getValidMoves(board, currentPlayer);

    if (validMoves.length === 0) {
      // Check if opponent can move
      const otherPlayer = currentPlayer === 'black' ? 'white' : 'black';
      const otherMoves = this.getValidMoves(board, otherPlayer);

      if (otherMoves.length === 0) {
        // Game over — evaluate final position
        return this.evaluateFinalBoard(board, aiPlayer);
      }

      // Pass turn to opponent
      return this.minimax(board, depth - 1, alpha, beta, !isMaximizing, aiPlayer);
    }

    if (isMaximizing) {
      let maxEval = -Infinity;
      for (const move of validMoves) {
        const newBoard = this.applyMove(board, currentPlayer, move);
        const evalScore = this.minimax(newBoard, depth - 1, alpha, beta, false, aiPlayer);
        maxEval = Math.max(maxEval, evalScore);
        alpha = Math.max(alpha, evalScore);
        if (beta <= alpha) break; // Prune
      }
      return maxEval;
    } else {
      let minEval = Infinity;
      for (const move of validMoves) {
        const newBoard = this.applyMove(board, currentPlayer, move);
        const evalScore = this.minimax(newBoard, depth - 1, alpha, beta, true, aiPlayer);
        minEval = Math.min(minEval, evalScore);
        beta = Math.min(beta, evalScore);
        if (beta <= alpha) break; // Prune
      }
      return minEval;
    }
  }

  /**
   * Evaluate the board position for the AI player.
   * Uses positional weights + disc count + mobility.
   */
  private evaluateBoard(board: CellState[][], aiPlayer: PlayerColor): number {
    const opponent = aiPlayer === 'black' ? 'white' : 'black';
    let score = 0;

    // Positional evaluation
    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] === aiPlayer) {
          score += POSITION_WEIGHTS[r][c];
        } else if (board[r][c] === opponent) {
          score -= POSITION_WEIGHTS[r][c];
        }
      }
    }

    // Mobility: number of valid moves
    const aiMoves = this.getValidMoves(board, aiPlayer).length;
    const oppMoves = this.getValidMoves(board, opponent).length;
    score += (aiMoves - oppMoves) * 5;

    return score;
  }

  /**
   * Evaluate the final board (game over) — pure disc count.
   */
  private evaluateFinalBoard(board: CellState[][], aiPlayer: PlayerColor): number {
    const opponent = aiPlayer === 'black' ? 'white' : 'black';
    let aiCount = 0;
    let oppCount = 0;

    for (let r = 0; r < BOARD_SIZE; r++) {
      for (let c = 0; c < BOARD_SIZE; c++) {
        if (board[r][c] === aiPlayer) aiCount++;
        else if (board[r][c] === opponent) oppCount++;
      }
    }

    if (aiCount > oppCount) return 10000 + (aiCount - oppCount);
    if (oppCount > aiCount) return -10000 - (oppCount - aiCount);
    return 0;
  }

  // =========================================================================
  // Board Helpers (used by AI — operate on raw board arrays)
  // =========================================================================

  private getValidMoves(board: CellState[][], player: PlayerColor): Position[] {
    const moves: Position[] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        if (board[row][col] === 'empty') {
          if (this.getFlippableDiscs(board, player, { row, col }).length > 0) {
            moves.push({ row, col });
          }
        }
      }
    }
    return moves;
  }

  private getFlippableDiscs(
    board: CellState[][],
    player: PlayerColor,
    position: Position
  ): Position[] {
    const { row, col } = position;
    if (board[row][col] !== 'empty') return [];

    const opponent = player === 'black' ? 'white' : 'black';
    const allFlipped: Position[] = [];

    for (const [dr, dc] of DIRECTIONS) {
      const flippedInDir: Position[] = [];
      let r = row + dr;
      let c = col + dc;

      while (
        r >= 0 && r < BOARD_SIZE &&
        c >= 0 && c < BOARD_SIZE &&
        board[r][c] === opponent
      ) {
        flippedInDir.push({ row: r, col: c });
        r += dr;
        c += dc;
      }

      if (
        flippedInDir.length > 0 &&
        r >= 0 && r < BOARD_SIZE &&
        c >= 0 && c < BOARD_SIZE &&
        board[r][c] === player
      ) {
        allFlipped.push(...flippedInDir);
      }
    }

    return allFlipped;
  }

  /**
   * Apply a move to a board and return the new board (does not mutate).
   */
  private applyMove(
    board: CellState[][],
    player: PlayerColor,
    position: Position
  ): CellState[][] {
    const newBoard = board.map(row => [...row]);
    newBoard[position.row][position.col] = player;

    const flipped = this.getFlippableDiscs(board, player, position);
    for (const disc of flipped) {
      newBoard[disc.row][disc.col] = player;
    }

    return newBoard;
  }
}
